/**
 * Ads API Service
 * Handles all ad-related API calls with role-based routing
 *
 * Users:  /api/v1/campaigns/:campaign_id/ad_units/:ad_unit_id/ads
 * Admins: /api/v1/administrator/campaigns/:campaign_id/ad_units/:ad_unit_id/ads
 */

import { apiClient } from './client'
import { getStoredRole } from '../../utils/token'

/**
 * Recursively flatten a JS object into Rails-style nested FormData keys.
 * `{ a: { b: 1, c: [2, 3] } }` ⟶ `a[b]=1`, `a[c][]=2`, `a[c][]=3`.
 * Skips `undefined` so callers can omit fields by leaving them undefined;
 * `null` is serialised as the literal string "" (Rails strong params treat
 * "" as "clear this field").
 */
function appendNested(formData: FormData, key: string, value: any): void {
  if (value === undefined) return
  if (value === null) {
    formData.append(key, '')
    return
  }
  if (value instanceof Blob) {
    formData.append(key, value)
    return
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      formData.append(`${key}[]`, '')
      return
    }
    // Numeric indices (`[0]`, `[1]`, …) rather than the generic `[]`
    // notation. Rails parses both identically for arrays of scalars,
    // but accepts_nested_attributes_for *requires* numeric indices so
    // it can match up sibling fields (e.g. a multipart file part at
    // `[0][video]` with metadata at `[0][video_type]`) into the same
    // nested-attributes hash. Using `[]` there causes Rails to treat
    // each part as a fresh array entry and the file gets detached
    // from its metadata. All current call sites are nested-attributes
    // payloads, so a single numeric-index policy is safe.
    value.forEach((v, i) => appendNested(formData, `${key}[${i}]`, v))
    return
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      appendNested(formData, `${key}[${k}]`, v)
    }
    return
  }
  formData.append(key, typeof value === 'boolean' ? String(value) : String(value))
}

// ─── Nested types ───

export interface AdVideo {
  id: number
  link: string | null
  uid: string | null
  title: string | null
  author: string | null
  likes: number | null
  dislikes: number | null
  video_type: string | null
  autoplay: boolean
  infini_link: string | null
  infini_uid: string | null
  youku_link: string | null
  youku_uid: string | null
  width_300x600: number
  width_320x480: number
  size: string | null
  category: string | null
  metadata: Record<string, any> | null
  video_url: string | null
  ad_id: number
  ad_unit_id: number | null
  variant_attribute_id: number | null
  created_at: string
  updated_at: string
}

/**
 * A variant attribute may carry an arbitrary number of `custom_image_N_url`
 * /`custom_image_N_multiple` pairs (the backend uses 1-based indexing, see
 * screenshots with custom_image_2..7). We keep `custom_image_1_*` typed for
 * back-compat with existing call sites but also expose an index signature
 * so callers can read `attr['custom_image_5_url']` without a cast.
 */
export interface AdVariantAttribute {
  id: number
  ad_id: number
  name: string | null
  data: Record<string, any>
  custom_image_1_url: string | null
  custom_image_1_multiple: boolean
  custom_text_1: string | null
  /**
   * Custom-template videos attached to this variant attribute. Mirrors
   * the `custom_image_N_url` slot model but lives as a proper array
   * because each entry carries a richer payload (file URL, type,
   * autoplay, S3 metadata, …) than the flat image-slot pair. Each
   * entry exposes the same shape as the top-level {@link AdVideo} type
   * minus a couple of ad-level only fields. Treated as optional here
   * because older variants pre-date the feature.
   */
  custom_template_videos?: AdVideo[] | null
  /**
   * Optional macro-key map for the variant's video slots, e.g.
   *   { "1": "video_1", "2": "hero_video" }
   * Symmetric with the ad-level `custom_image_macro_keys` — the editor
   * uses it to label the Custom Video sub-tabs AND to pre-create empty
   * upload slots for indices the backend hasn't seeded into
   * `custom_template_videos[]` yet. Absent on variants pre-dating the
   * feature.
   */
  custom_video_macro_keys?: Record<string | number, string> | null
  created_at: string
  updated_at: string
  [key: string]: any
}

export interface AdMacro {
  id: number
  name: string
  condition: string
  value: string
  ad_id: number
  ad_unit_id: number | null
  smart_tag_id: number | null
}

export interface AdTracker {
  id: number
  name: string
  impression_url: string | null
  click_url: string | null
  ad_unit_size: string | null
  macro_value: string | null
  ad_id: number
  ad_unit_id: number | null
  created_at: string
  updated_at: string
}

export interface AdPage {
  id: number
  name: string
  url: string
}

export interface AdLeadForm {
  id: number
  [key: string]: any
}

// ─── Ad types ───

export interface AdListItem {
  id: number
  title: string | null
  description: string | null
  destination_url: string | null
  ad_unit_id: number
  status: string
  content_type: string | null
  default: boolean
  action_text: string | null
  has_action: boolean
  button_color: string | null
  image_url: string | null
  created_at: string
  updated_at: string
  variant_attribute?: {
    id: number
    name: string | null
  } | null
}

export interface Ad extends AdListItem {
  has_description: boolean
  show_title: boolean
  image_only: boolean
  overlap_type: string | null
  impression_tracker: string | null
  include_like_share: boolean
  short_url: string | null
  og_description: string | null
  label_title: string | null
  label_font_color: string | null
  label_background_color: string | null
  place_to_search: string | null
  product_id: string | null
  ps_button_text: string | null
  places_search_radius: string | null
  provider_metadata: any | null
  headline_fb: string | null
  adset_id: number | null
  publish_date: string | null
  publish_time: string | null
  unpublished_date: string | null
  unpublished_time: string | null
  video_5_tracker: string | null
  video_25_tracker: string | null
  video_50_tracker: string | null
  video_75_tracker: string | null
  video_100_tracker: string | null
  ad_unit?: {
    id: number
    name: string
    content_type: string
    campaign_id: number
    sizes?: string[]
    created_at: string
    updated_at: string
  }
  video: AdVideo | null
  audio: any | null
  lead_form: AdLeadForm | null
  variant_attribute: AdVariantAttribute | null
  macros: AdMacro[]
  trackers: AdTracker[]
  pictures: any[]
  pages: AdPage[]
  /**
   * Backend mapping that gives each `custom_image_N_url` slot a
   * human-meaningful macro key.
   *
   * Shape (sent as a JS object keyed by the 1-based slot index — Rails
   * serialises both string and integer keys interchangeably, so we
   * accept either):
   *
   *   {
   *     "1": "custom_image_1_product_image_1",
   *     "2": "custom_image_2_product_image_2",
   *     …
   *   }
   *
   * The value is the *full* macro identifier; the frontend strips the
   * `custom_image_<N>_` prefix and title-cases the remainder to derive
   * the tab label users see in the Variant Attribute editor.
   */
  custom_image_macro_keys?: Record<string | number, string> | null
}

// ─── Request/response shapes ───

export interface AdsQueryParams {
  page?: number
  per_page?: number
  q?: string
}

export interface AdsMeta {
  current_page: number
  per_page: number
  total_pages: number
  total_count: number
}

interface AdsListResponse {
  ads: AdListItem[]
  meta: AdsMeta
}

interface AdDetailResponse {
  ad: Ad
}

interface AdResponse {
  ad: AdListItem
}

/**
 * Shape of `GET /ads/new`. Rails' `new` action returns a not-yet-persisted
 * ad template that the frontend uses to seed the "Add Variant" form:
 * defaults, empty associations, and — crucially — the macro-keys maps
 * (`custom_image_macro_keys` on the ad, and
 * `variant_attribute.custom_video_macro_keys` on the nested attribute)
 * that tell the editor which image/video slot tabs to render even
 * before the user has touched anything. Every field is optional because
 * a template ad has no persisted `id` / `created_at` and most scalar
 * fields come back `null` or omitted.
 *
 * We alias `Partial<Ad>` rather than defining a fresh interface so a
 * template can flow straight into the existing `populateForm(ad)`
 * hydration path without a bespoke mapper.
 */
export type NewAdTemplate = Partial<Ad>

interface NewAdResponse {
  ad: NewAdTemplate
}

// ─── Path builder ───

function getAdsBasePath(campaignId: number, adUnitId: number): string {
  const role = getStoredRole()
  const prefix = role === 'admin' ? '/administrator/campaigns' : '/campaigns'
  return `${prefix}/${campaignId}/ad_units/${adUnitId}/ads`
}

function buildQueryString(params: AdsQueryParams): string {
  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append('page', params.page.toString())
  if (params.per_page) queryParams.append('per_page', params.per_page.toString())
  if (params.q) queryParams.append('q', params.q)
  const qs = queryParams.toString()
  return qs ? `?${qs}` : ''
}

// ─── API methods ───

export const adsApi = {
  getAds: async (
    campaignId: number,
    adUnitId: number,
    params: AdsQueryParams = {}
  ): Promise<{ ads: AdListItem[]; meta: AdsMeta }> => {
    const qs = buildQueryString(params)
    const response = await apiClient.get<AdsListResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}${qs}`,
      { skipRoleRouting: true }
    )
    return {
      ads: response.ads || [],
      meta: response.meta,
    }
  },

  getAd: async (
    campaignId: number,
    adUnitId: number,
    adId: number
  ): Promise<Ad> => {
    const response = await apiClient.get<AdDetailResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}/${adId}`,
      { skipRoleRouting: true }
    )
    return response.ad
  },

  /**
   * Fetch the "new ad" template used to seed the Add Variant form when
   * the user clicks the `+` button on the variants panel.
   *
   *   GET /api/v1/campaigns/:campaign_id/ad_units/:ad_unit_id/ads/new
   *   GET /api/v1/administrator/campaigns/:campaign_id/ad_units/:ad_unit_id/ads/new
   *
   * Role selection is automatic via {@link getAdsBasePath} (same pattern
   * every method on this service follows) — admins get the
   * `/administrator` prefix, everyone else hits the user route.
   *
   * The response envelope mirrors {@link getAd} (`{ ad: ... }`), but
   * the returned ad is a not-yet-persisted template: it carries
   * defaults + the macro-keys maps the editor needs to render the
   * correct set of image/video upload slots and text placeholder
   * fields for a fresh variant. See {@link NewAdTemplate} for the
   * exact shape.
   */
  getNewAd: async (
    campaignId: number,
    adUnitId: number
  ): Promise<NewAdTemplate> => {
    const response = await apiClient.get<NewAdResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}/new`,
      { skipRoleRouting: true }
    )
    return response.ad
  },

  createAd: async (
    campaignId: number,
    adUnitId: number,
    data: Record<string, any>
  ): Promise<AdListItem> => {
    const response = await apiClient.post<AdResponse>(
      getAdsBasePath(campaignId, adUnitId),
      { ad: data },
      { skipRoleRouting: true }
    )
    return response.ad
  },

  /**
   * Multipart variant of {@link createAd}. Symmetric with
   * {@link updateAdMultipart} — use when the create payload contains
   * binary fields (uploaded custom image slots, new
   * `custom_template_videos_attributes[N][video]` files, etc.) that the
   * backend expects as actual multipart parts rather than JSON.
   *
   * `data` is flattened to `ad[...]` keys (with `[index]` / `[key]`
   * segments for arrays / nested objects, matching Rails'
   * `accepts_nested_attributes_for` shape). `files` is a flat map of
   * *fully-qualified* form-field names → File, so the caller controls
   * the exact key (e.g.
   * `ad[variant_attribute_attributes][custom_image_1]` or
   * `ad[variant_attribute_attributes][custom_template_videos_attributes][0][video]`)
   * without us hard-coding a mapping here.
   */
  createAdMultipart: async (
    campaignId: number,
    adUnitId: number,
    data: Record<string, any>,
    files: Record<string, File>
  ): Promise<AdListItem> => {
    const formData = new FormData()
    appendNested(formData, 'ad', data)
    for (const [field, file] of Object.entries(files)) {
      formData.append(field, file)
    }
    const response = await apiClient.postFormData<AdResponse>(
      getAdsBasePath(campaignId, adUnitId),
      formData,
      { skipRoleRouting: true }
    )
    return response.ad
  },

  updateAd: async (
    campaignId: number,
    adUnitId: number,
    adId: number,
    data: Record<string, any>
  ): Promise<AdListItem> => {
    const response = await apiClient.patch<AdResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}/${adId}`,
      { ad: data },
      { skipRoleRouting: true }
    )
    return response.ad
  },

  /**
   * Multipart variant of updateAd. Use when the payload contains binary
   * fields (e.g. replaced custom_image_N files) that the backend expects
   * as actual file uploads under
   *   `ad[variant_attribute_attributes][custom_image_N]`.
   *
   * `files` is a flat map of *fully-qualified* form field names → File so
   * the caller controls the exact key (matches Rails' permitted-params /
   * Active Storage / Shrine conventions without us having to encode the
   * mapping here).
   *
   * Everything in `data` is flattened to `ad[...]` keys (with
   * `[index]` / `[key]` segments for arrays / nested objects) and appended
   * alongside the files. Uses PUT because `putFormData` is the only
   * multipart-capable verb the client exposes today; Rails routes treat
   * PUT and PATCH identically for `update`.
   */
  updateAdMultipart: async (
    campaignId: number,
    adUnitId: number,
    adId: number,
    data: Record<string, any>,
    files: Record<string, File>
  ): Promise<AdListItem> => {
    const formData = new FormData()
    appendNested(formData, 'ad', data)
    for (const [field, file] of Object.entries(files)) {
      formData.append(field, file)
    }
    const response = await apiClient.putFormData<AdResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}/${adId}`,
      formData,
      { skipRoleRouting: true }
    )
    return response.ad
  },

  deleteAd: async (
    campaignId: number,
    adUnitId: number,
    adId: number
  ): Promise<void> => {
    await apiClient.delete(
      `${getAdsBasePath(campaignId, adUnitId)}/${adId}`,
      { skipRoleRouting: true }
    )
  },

  copyAd: async (
    campaignId: number,
    adUnitId: number,
    adId: number
  ): Promise<AdListItem> => {
    const response = await apiClient.get<AdResponse>(
      `${getAdsBasePath(campaignId, adUnitId)}/${adId}/copy`,
      { skipRoleRouting: true }
    )
    return response.ad
  },
}
